<script lang="ts" setup>
import {ref} from "vue";
import ModalComponent from "@/components/ModalComponent.vue";
import BtnCta from "@/components/buttons/BtnCta.vue";
import Btn from "@/components/buttons/Btn.vue";

import IconPencil from '~icons/tabler/pencil';
import IconTrash from '~icons/tabler/trash';
import IconUser from '~icons/tabler/user';
import IconCompany from '~icons/tabler/building';
import IconHouse from '~icons/tabler/home';
import IconDimensions from '~icons/tabler/cube';
import IconSearch from '~icons/tabler/search';
import IconGroup from '~icons/tabler/users';
import IconBell from '~icons/tabler/bell';
import IconDashboard from '~icons/tabler/layout-dashboard';
import IconSalaries from '~icons/tabler/cash';
import IconBuy from '~icons/tabler/shopping-cart';
import IconVacation from '~icons/tabler/beach';
import IconAbsence from '~icons/tabler/user-off';
import IconSickLeave from '~icons/tabler/medical-cross';
import IconWithholdings from '~icons/tabler/cash-off';
import IconLock from '~icons/tabler/lock';
import IconPayouts from '~icons/tabler/cash-banknote';
import IconCalendar from '~icons/tabler/calendar';
import IconCalculator from '~icons/tabler/calculator';
import IconSettings from '~icons/tabler/settings';
import IconAdd from '~icons/tabler/plus';
import IconAttachment from '~icons/tabler/paperclip';
import IconComment from '~icons/tabler/message';
import IconEmail from '~icons/tabler/mail';
import IconWarning from '~icons/tabler/alert-triangle';
import IconLogOut from '~icons/tabler/logout';
import IconHelp from '~icons/tabler/help-circle';
import IconLightBulb from '~icons/tabler/bulb';
import IconThermometer from '~icons/tabler/thermometer';
import IconDetails from '~icons/tabler/info-circle';
import IconList from '~icons/tabler/list';
import IconDotsVertical from '~icons/tabler/dots-vertical';
import IconShowAll from '~icons/tabler/eye';
import IconUserGear from '~icons/tabler/user-cog';
import IconContract from '~icons/tabler/file';
import IconLicense from '~icons/tabler/license';

import IconCheckboxBlankOutline from '~icons/tabler/square';
import IconCheckboxBlank from '~icons/tabler/square-filled';
import IconCheckboxMarkedOutline from '~icons/tabler/square-check';
import IconCheckboxMarked from '~icons/tabler/square-check-filled';
import IconCheckboxIntermediate from '~icons/tabler/square-minus';
import IconCheckboxMultipleBlankOutline from '~icons/tabler/copy';
import IconCheckboxMultipleMarkedOutline from '~icons/tabler/list-check';
import IconCheckboxMultipleMarked from '~icons/tabler/checks';
import IconCloseBoxOutline from '~icons/tabler/square-x';
import IconCloseBox from '~icons/tabler/square-x-filled';
import IconMinusBoxOutline from '~icons/tabler/square-minus';
import IconMinusBox from '~icons/tabler/square-minus-filled';
import IconCheck from '~icons/tabler/check';
import IconCheckBold from '~icons/tabler/check';
import IconCheckCircleOutline from '~icons/tabler/circle-check';
import IconCheckCircle from '~icons/tabler/circle-check-filled';
import IconRadioboxBlank from '~icons/tabler/circle';
import IconRadioboxMarked from '~icons/tabler/circle-dot';

defineOptions({
  name: 'DemoApp'
});

window.appConfig = {
  apiEndpoint: import.meta.env.VITE_API_URL || 'localhost:44363',
  authenticationApiEndpoint: '',
  localization: 'EE',
  product: 'PALK',
};

const showModal = ref(false);
</script>

<template>
  <div class="demo-app">
    <h2>Demo Area</h2>
    <p>This area is available for testing new components.</p>

    <section class="icon-gallery">
      <h3>Random icons (unplugin-icons)</h3>
      <ul class="icon-row">
        <li><IconPencil /><span>edit</span></li>
        <li><IconTrash /><span>delete</span></li>
        <li><IconUser /><span>user</span></li>
        <li><IconCompany /><span>company</span></li>
        <li><IconHouse /><span>house</span></li>
        <li><IconDimensions /><span>dimensions</span></li>
        <li><IconSearch /><span>search</span></li>
        <li><IconGroup /><span>group</span></li>
        <li><IconBell /><span>notification</span></li>
        <li><IconDashboard /><span>dashboard</span></li>
        <li><IconSalaries /><span>salaries</span></li>
        <li><IconBuy /><span>buy</span></li>
        <li><IconVacation /><span>vacation</span></li>
        <li><IconAbsence /><span>absence</span></li>
        <li><IconSickLeave /><span>sick leave</span></li>
        <li><IconWithholdings /><span>withholdings</span></li>
        <li><IconLock /><span>lock</span></li>
        <li><IconPayouts /><span>payouts</span></li>
        <li><IconCalendar /><span>calendar</span></li>
        <li><IconCalculator /><span>calculator</span></li>
        <li><IconSettings /><span>settings</span></li>
        <li><IconAdd /><span>add</span></li>
        <li><IconAttachment /><span>attachment</span></li>
        <li><IconComment /><span>comment</span></li>
        <li><IconEmail /><span>email</span></li>
        <li><IconWarning /><span>warning</span></li>
        <li><IconLogOut /><span>log out</span></li>
        <li><IconHelp /><span>help</span></li>
        <li><IconLightBulb /><span>tip</span></li>
        <li><IconThermometer /><span>thermometer</span></li>
        <li><IconDetails /><span>details</span></li>
        <li><IconList /><span>list</span></li>
        <li><IconDotsVertical /><span>more</span></li>
        <li><IconShowAll /><span>show all</span></li>
        <li><IconUserGear /><span>user settings</span></li>
        <li><IconContract /><span>contract</span></li>
        <li><IconLicense /><span>license</span></li>
      </ul>
    </section>

    <section class="icon-gallery">
      <h3>Checkbox icons</h3>
      <ul class="icon-row">
        <li><IconCheckboxBlankOutline /><span>blank outline</span></li>
        <li><IconCheckboxBlank /><span>blank filled</span></li>
        <li><IconCheckboxMarkedOutline /><span>marked outline</span></li>
        <li><IconCheckboxMarked /><span>marked</span></li>
        <li><IconCheckboxIntermediate /><span>indeterminate</span></li>
        <li><IconCheckboxMultipleBlankOutline /><span>multi blank</span></li>
        <li><IconCheckboxMultipleMarkedOutline /><span>multi marked outline</span></li>
        <li><IconCheckboxMultipleMarked /><span>multi marked</span></li>
        <li><IconCloseBoxOutline /><span>close box outline</span></li>
        <li><IconCloseBox /><span>close box</span></li>
        <li><IconMinusBoxOutline /><span>minus box outline</span></li>
        <li><IconMinusBox /><span>minus box</span></li>
        <li><IconCheck /><span>check</span></li>
        <li><IconCheckBold /><span>check bold</span></li>
        <li><IconCheckCircleOutline /><span>check circle outline</span></li>
        <li><IconCheckCircle /><span>check circle</span></li>
        <li><IconRadioboxBlank /><span>radio blank</span></li>
        <li><IconRadioboxMarked /><span>radio marked</span></li>
      </ul>
    </section>

    <section class="modal-demo">
      <h3>Modal demo</h3>
      <BtnCta @click="showModal = true">Open modal</BtnCta>
    </section>

    <ModalComponent :show="showModal" @close="showModal = false">
      <template #header>
        <h2>Hello from the demo modal</h2>
      </template>
      <p>Close button in the header uses <code>IconBtnClose</code> with <code>ic:baseline-close</code>.</p>
      <p>Hover the X to see the red flip.</p>
      <template #footer>
        <Btn @click="showModal = false">Cancel</Btn>
        <BtnCta @click="showModal = false">OK</BtnCta>
      </template>
    </ModalComponent>
  </div>
</template>

<style scoped>
.icon-gallery {
  margin-top: 24px;
}

.icon-row {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 0;
  margin: 12px 0 0;
}

.icon-row li {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 110px;
  padding: 12px;
  font-size: 11px;
  color: #555;
}

.icon-row svg {
  width: 28px;
  height: 28px;
  color: #919191;
}

.modal-demo {
  margin-top: 32px;
}
</style>
